import { gsap } from "gsap";
import type { CardController } from "./CardController";

const DRAG_SENSITIVITY = 0.0085;
const Z_DRAG_SENSITIVITY = 0.012;
const INERTIA_DAMPING = 0.94;
const INERTIA_THRESHOLD = 0.0008;
const DOUBLE_TAP_MS = 320;
const SNAP_BACK_DELAY_MS = 650;
const SNAP_BACK_DURATION_S = 0.85;
const CORNER_THRESHOLD = 0.6;

type DragMode = "orbit" | "roll";

/**
 * Pointer-driven rotation for the CSS-3D card. Tracks velocity for inertia,
 * detects double-tap to flip 180° around Y, and after a quiet period after
 * release snaps the card back to its nearest stable orientation (multiples
 * of π) so the layout always settles in a "nice" pose.
 */
export class DragController {
  private readonly card: CardController;
  private readonly target: HTMLElement;
  private dragging = false;
  private velX = 0;
  private velY = 0;
  private velZ = 0;
  private lastPointer: { x: number; y: number; time: number } | null = null;
  private lastTapTime = 0;
  private rafId = 0;
  private enabled = true;
  private idleTimer: number | null = null;
  private snapTween: gsap.core.Tween | null = null;
  private dragMode: DragMode = "orbit";
  private pivotCenter: { x: number; y: number } = { x: 0, y: 0 };
  private initialPointerAngle = 0;
  private initialRotZ = 0;
  private readonly activePointers = new Map<number, { x: number; y: number }>();
  private pinchState: {
    initialDist: number;
    initialMidX: number;
    initialMidY: number;
    baseTx: number;
    baseTy: number;
    baseScale: number;
  } | null = null;

  constructor(card: CardController) {
    this.card = card;
    this.target = card.root;
    this.target.addEventListener("pointerdown", this.onPointerDown);
    this.target.addEventListener("pointermove", this.onPointerMove);
    this.target.addEventListener("pointerup", this.onPointerUp);
    this.target.addEventListener("pointercancel", this.onPointerUp);
    this.target.addEventListener("pointerleave", this.onPointerUp);
  }

  enable(): void {
    this.enabled = true;
  }

  disable(): void {
    this.enabled = false;
    this.dragging = false;
    this.velX = 0;
    this.velY = 0;
    this.velZ = 0;
    this.cancelSnapBack();
    this.card.endDrag();
  }

  private onPointerDown = (event: PointerEvent): void => {
    if (!this.enabled) return;
    const now = performance.now();
    const onLink = !!(event.target as Element | null)?.closest?.("a");

    this.activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    // Two or more pointers down → pinch / pan mode. Stop any single-finger
    // drag in progress and hand off control to the pinch math.
    if (this.activePointers.size >= 2) {
      this.dragging = false;
      this.lastPointer = null;
      this.stopInertia();
      this.lastTapTime = 0;
      this.beginPinch();
      return;
    }

    this.cancelSnapBack();

    if (!onLink && now - this.lastTapTime < DOUBLE_TAP_MS) {
      this.lastTapTime = 0;
      this.flip();
      return;
    }
    this.lastTapTime = onLink ? 0 : now;

    (event.target as Element).setPointerCapture?.(event.pointerId);
    this.dragging = true;
    this.card.externalControl = true;
    this.card.suspendFloat();
    this.card.beginDrag();
    this.velX = 0;
    this.velY = 0;
    this.velZ = 0;
    this.lastPointer = { x: event.clientX, y: event.clientY, time: now };

    const rect = this.target.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const halfW = Math.max(1, rect.width / 2);
    const halfH = Math.max(1, rect.height / 2);
    const nx = (event.clientX - cx) / halfW;
    const ny = (event.clientY - cy) / halfH;
    const inCorner = Math.abs(nx) >= CORNER_THRESHOLD && Math.abs(ny) >= CORNER_THRESHOLD;

    if (event.shiftKey || inCorner) {
      this.dragMode = "roll";
      this.pivotCenter = { x: cx, y: cy };
      this.initialPointerAngle = Math.atan2(event.clientY - cy, event.clientX - cx);
      this.initialRotZ = this.card.rotation.z;
    } else {
      this.dragMode = "orbit";
    }

    this.stopInertia();
  };

  private onPointerMove = (event: PointerEvent): void => {
    const stored = this.activePointers.get(event.pointerId);
    if (stored) {
      stored.x = event.clientX;
      stored.y = event.clientY;
    }

    if (this.pinchState && this.activePointers.size >= 2) {
      this.updatePinch();
      return;
    }

    if (!this.dragging || !this.lastPointer) return;
    const dx = event.clientX - this.lastPointer.x;
    const dy = event.clientY - this.lastPointer.y;
    const now = performance.now();
    const dt = Math.max(1, now - this.lastPointer.time);
    const frameScale = dt / 16.6;

    if (this.dragMode === "roll") {
      if (event.shiftKey) {
        const dz = dx * Z_DRAG_SENSITIVITY;
        this.card.addRotation(0, 0, dz);
        this.velX = 0;
        this.velY = 0;
        this.velZ = dz / frameScale;
      } else {
        // Corner-drag: absolute angle from card center, like Illustrator's
        // rotate handle. Velocity tracked via per-frame delta.
        const ang = Math.atan2(event.clientY - this.pivotCenter.y, event.clientX - this.pivotCenter.x);
        const prevAng = Math.atan2(this.lastPointer.y - this.pivotCenter.y, this.lastPointer.x - this.pivotCenter.x);
        const targetZ = this.initialRotZ + (ang - this.initialPointerAngle);
        const { x, y } = this.card.rotation;
        this.card.setRotation(x, y, targetZ);
        this.velX = 0;
        this.velY = 0;
        this.velZ = (ang - prevAng) / frameScale;
      }
    } else {
      this.card.addRotation(dx * DRAG_SENSITIVITY, dy * DRAG_SENSITIVITY * 0.6, 0);
      this.velX = (dx * DRAG_SENSITIVITY) / frameScale;
      this.velY = (dy * DRAG_SENSITIVITY * 0.6) / frameScale;
      this.velZ = 0;
    }

    this.lastPointer = { x: event.clientX, y: event.clientY, time: now };
  };

  private onPointerUp = (event: PointerEvent): void => {
    this.activePointers.delete(event.pointerId);
    (event.target as Element).releasePointerCapture?.(event.pointerId);

    if (this.pinchState) {
      if (this.activePointers.size < 2) {
        // Leave pinch mode entirely. Don't auto-resume single-finger drag
        // with the remaining pointer — wait for the next pointerdown so
        // the card doesn't jump.
        this.pinchState = null;
      }
      return;
    }

    if (!this.dragging) return;
    this.dragging = false;
    this.lastPointer = null;
    this.card.endDrag();
    this.startInertia();
  };

  private startInertia(): void {
    this.stopInertia();
    const tick = (): void => {
      this.velX *= INERTIA_DAMPING;
      this.velY *= INERTIA_DAMPING;
      this.velZ *= INERTIA_DAMPING;
      this.card.addRotation(this.velX, this.velY, this.velZ);
      if (
        Math.abs(this.velX) < INERTIA_THRESHOLD &&
        Math.abs(this.velY) < INERTIA_THRESHOLD &&
        Math.abs(this.velZ) < INERTIA_THRESHOLD
      ) {
        this.scheduleSnapBack();
        return;
      }
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private stopInertia(): void {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }

  private scheduleSnapBack(): void {
    this.cancelSnapBack();
    this.idleTimer = window.setTimeout(() => {
      this.idleTimer = null;
      this.snapToHome();
    }, SNAP_BACK_DELAY_MS);
  }

  private cancelSnapBack(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    if (this.snapTween) {
      this.snapTween.kill();
      this.snapTween = null;
    }
  }

  /**
   * Snap to the nearest visually-equivalent neutral pose: rotateX → 0,
   * rotateY → nearest multiple of π. That way a small accidental drag
   * unwinds back to facing front (or back, if the card was flipped),
   * but a deliberate flip is preserved.
   */
  private snapToHome(): void {
    const start = this.card.rotation;
    const targetY = Math.round(start.y / Math.PI) * Math.PI;
    const state = { x: start.x, y: start.y, z: start.z };
    this.snapTween = gsap.to(state, {
      x: 0,
      y: targetY,
      z: 0,
      duration: SNAP_BACK_DURATION_S,
      ease: "power3.inOut",
      onUpdate: () => this.card.setRotation(state.x, state.y, state.z),
      onComplete: () => {
        this.snapTween = null;
        this.card.externalControl = false;
        this.card.resumeFloat();
      },
    });
  }

  private beginPinch(): void {
    const [a, b] = Array.from(this.activePointers.values());
    if (!a || !b) return;
    const dist = Math.hypot(b.x - a.x, b.y - a.y);
    const base = this.card.transform;
    this.pinchState = {
      initialDist: Math.max(1, dist),
      initialMidX: (a.x + b.x) / 2,
      initialMidY: (a.y + b.y) / 2,
      baseTx: base.tx,
      baseTy: base.ty,
      baseScale: base.scl,
    };
    this.card.externalControl = true;
    this.card.suspendFloat();
    this.card.endDrag();
    this.cancelSnapBack();
  }

  private updatePinch(): void {
    if (!this.pinchState) return;
    const [a, b] = Array.from(this.activePointers.values());
    if (!a || !b) return;
    const dist = Math.hypot(b.x - a.x, b.y - a.y);
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const ratio = dist / this.pinchState.initialDist;
    const nextScale = Math.min(3.0, Math.max(0.4, this.pinchState.baseScale * ratio));
    const nextTx = this.pinchState.baseTx + (mx - this.pinchState.initialMidX);
    const nextTy = this.pinchState.baseTy + (my - this.pinchState.initialMidY);
    this.card.setTransform(nextTx, nextTy, nextScale);
  }

  private flip(): void {
    this.cancelSnapBack();
    this.card.resetTransform();
    const start = this.card.rotation;
    this.card.externalControl = true;
    this.card.suspendFloat();
    const state = { x: start.x, y: start.y, z: start.z };
    gsap.to(state, {
      x: 0,
      y: start.y + Math.PI,
      z: 0,
      duration: 0.9,
      ease: "power3.inOut",
      onUpdate: () => this.card.setRotation(state.x, state.y, state.z),
      onComplete: () => {
        // Settle the float-resume after a brief pause so the user can
        // appreciate the flipped face before the card starts wandering.
        this.scheduleSnapBack();
      },
    });
  }

  dispose(): void {
    this.target.removeEventListener("pointerdown", this.onPointerDown);
    this.target.removeEventListener("pointermove", this.onPointerMove);
    this.target.removeEventListener("pointerup", this.onPointerUp);
    this.target.removeEventListener("pointercancel", this.onPointerUp);
    this.target.removeEventListener("pointerleave", this.onPointerUp);
    this.cancelSnapBack();
    this.stopInertia();
  }
}
